import { create } from 'zustand';
import TagManager from 'react-gtm-module';
import { v4 as uuidV4 } from 'uuid';
import Cookies from 'js-cookie';

interface ImpressionItem {
    item_id: string;
    item_name: string;
    index: number;
    item_brand: string;
    price: number;
    quantity: number;
    item_category: string;
    item_category2: string;
    item_category3: string;
    item_category4: string;
    item_category5: string;
    item_list_id: string;
    item_list_name: string;
    item_list_location: string;
    discount: number;
}

interface TrackingStore {
    impressions: {
        queue: ImpressionItem[];
        consume: () => void;
        productImpression: (args: {
            id: string;
            analytics?: any;
            price: number;
            brand?: string;
            discount?: string | number;
            list?: { id?: string; name?: string; location?: string };
        }) => void;
    };
}

export function convertToSnakeCase(input: string) {
    if (typeof input !== 'string') return input;
    return input.replace(/ /g, '_').toLowerCase();
}

export function generateEventId() {
    return uuidV4();
}

export const useTrackingStore = create<TrackingStore>((set, get) => ({
    impressions: {
        queue: [],
        consume() {
            const { queue } = get().impressions;
            if (queue.length === 0) return;

            const eventId = generateEventId();
            TagManager.dataLayer({ dataLayer: { ecommerce: null } });

            const list = queue[0]?.item_list_id
                ? {
                    id: queue[0].item_list_id,
                    name: queue[0].item_list_name,
                    location: queue[0].item_list_location,
                }
                : {};

            const tempLayer = {
                dataLayer: {
                    event: 'product_list_impression',
                    event_id: eventId,
                    ecommerce: {
                        item_list_id: list?.id || '',
                        item_list_name: convertToSnakeCase(list?.name || ''),
                        item_list_location: list?.location || '',
                        value: queue.reduce((sum, p) => sum + (p.price || 0), 0),
                        cart_ref: Cookies.get(
                            process.env.NEXT_PUBLIC_STOREFRONT_SESSION_COOKIE || ''
                        ),
                        items: queue,
                    },
                },
            };

            TagManager.dataLayer(tempLayer);

            // clear queue after consume
            set((state) => ({
                impressions: {
                    ...state.impressions,
                    queue: [],
                    consume: state.impressions.consume,
                    productImpression: state.impressions.productImpression,
                },
            }));
        },
        productImpression({ id, analytics, price, brand, discount, list = {} }) {
            const item: ImpressionItem = {
                item_id: id,
                item_name: convertToSnakeCase(analytics?.item_name || ''),
                index: 0,
                item_brand: convertToSnakeCase(brand || ''),
                price,
                quantity: 1,
                item_category: convertToSnakeCase(analytics?.item_category || ''),
                item_category2: convertToSnakeCase(analytics?.item_category2 || ''),
                item_category3: convertToSnakeCase(analytics?.item_category3 || ''),
                item_category4: convertToSnakeCase(analytics?.item_category4 || ''),
                item_category5: convertToSnakeCase(analytics?.item_category5 || ''),
                item_list_id: list?.id || '',
                item_list_name: convertToSnakeCase(list?.name || ''),
                item_list_location: list?.location || '',
                discount: parseFloat(String(discount)) || 0,
            };

            set((state) => {
                const newQueue = [...state.impressions.queue, item];
                return {
                    impressions: {
                        ...state.impressions,
                        queue: newQueue,
                    },
                };
            });

            // flush after 25
            if (get().impressions.queue.length >= 25) {
                get().impressions.consume();
            }
        },
    },
}));
